import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

interface Message {
  id: string;
  project_id: string;
  sender_id: string;
  sender_name: string;
  sender_role: string;
  message: string;
  attachment?: any;
  is_read: boolean;
  created_at: string;
}

interface Project {
  id: string;
  title: string;
  client_id: string;
  freelancer_id?: string;
  client_name: string;
  freelancer_name?: string;
  status: string;
}

const ProjectMessaging: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load user data
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (!token || !userData) {
          navigate('/signin');
          return;
        }

        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error loading user data:', error);
        navigate('/signin');
      }
    };

    loadUserData();
  }, [navigate]);

  // Fetch project and messages
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      const [projectResponse, messagesResponse] = await Promise.all([
        axios.get(`http://localhost:5000/api/projects/${projectId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`http://localhost:5000/api/projects/${projectId}/messages`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (projectResponse.data.success) {
        setProject(projectResponse.data.data.project);
      }

      if (messagesResponse.data.success) {
        setMessages(messagesResponse.data.data.messages);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      navigate('/messages');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    if (user && projectId) {
      fetchData();
    }
  }, [user, projectId, fetchData]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !projectId) return;

    try {
      setIsSending(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `http://localhost:5000/api/projects/${projectId}/messages`,
        { message: newMessage },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setNewMessage('');
        fetchData(); // Refresh messages
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message');
    } finally {
      setIsSending(false);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getOtherParty = () => {
    if (!project || !user) return null;
    
    if (user.role === 'client' && project.freelancer_name) {
      return {
        name: project.freelancer_name,
        role: 'freelancer'
      };
    } else if (user.role === 'freelancer') {
      return {
        name: project.client_name,
        role: 'client'
      };
    }
    return null;
  };

  const canSendMessages = () => {
    if (!project || !user) return false;
    
    // Admin and manager can always message
    if (user.role === 'admin' || user.role === 'manager') return true;
    
    // Client can message if project has a freelancer
    if (user.role === 'client' && project.freelancer_id) return true;
    
    // Freelancer can message if assigned to project
    if (user.role === 'freelancer' && project.freelancer_id === user.id) return true;
    
    return false;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  if (!project || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Project Not Found</h2>
          <button 
            onClick={() => navigate('/messages')}
            className="btn btn-primary"
          >
            Back to Messages
          </button>
        </div>
      </div>
    );
  }

  const otherParty = getOtherParty();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="flex items-center gap-4">
            <div className="navbar-brand">
              <div className="brand-logo">NS</div>
              <span className="brand-text">NexSync</span>
            </div>
            
            <button 
              type="button"
              onClick={() => navigate('/messages')}
              className="btn btn-ghost btn-sm"
              title="Back to Messages"
            >
              ← Back
            </button>
          </div>
          
          <div className="navbar-actions">
            <div className="user-info">
              <div className="user-avatar">
                {user.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="user-details">
                <span className="user-name">{user.username.toUpperCase()}</span>
                <div className="user-meta">
                  <span className={`role-badge role-${user.role}`}>{user.role}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="dashboard-container">
        <div className="dashboard-content">
          {/* Project Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{project.title}</h1>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>💬 Project Messages</span>
                  {otherParty && (
                    <span>
                      {otherParty.role === 'client' ? '👤 Client:' : '👨‍💻 Freelancer:'} {otherParty.name}
                    </span>
                  )}
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    project.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    project.status === 'active' ? 'bg-blue-100 text-blue-800' :
                    project.status === 'completed' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {project.status}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <button 
                  type="button"
                  onClick={() => navigate(`/${user.role}/projects/${projectId}`)}
                  className="btn btn-secondary btn-sm"
                >
                  View Project
                </button>
              </div>
            </div>
          </div>

          {/* Messages Container */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="h-[600px] flex flex-col">
              {/* Messages Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Messages ({messages.length})
                  </h2>
                  <div className="text-sm text-gray-600">
                    {otherParty ? `Chatting with ${otherParty.name}` : 'Project Messages'}
                  </div>
                </div>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-6">
                {messages.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-gray-400 text-4xl mb-4">💬</div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Messages Yet</h3>
                      <p className="text-gray-600">
                        Start the conversation by sending a message below.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {messages.map((message, index) => {
                      const isOwnMessage = message.sender_id === user.id;
                      const showDate = index === 0 || 
                        new Date(message.created_at).toDateString() !== 
                        new Date(messages[index - 1].created_at).toDateString();

                      return (
                        <div key={message.id}>
                          {/* Date Separator */}
                          {showDate && (
                            <div className="flex items-center justify-center my-6">
                              <div className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                                {formatDate(message.created_at)}
                              </div>
                            </div>
                          )}

                          {/* Message */}
                          <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[70%] ${isOwnMessage ? 'order-2' : 'order-1'}`}>
                              <div className={`rounded-2xl px-4 py-3 ${
                                isOwnMessage
                                  ? 'bg-blue-100 text-blue-900 rounded-br-none'
                                  : 'bg-gray-100 text-gray-900 rounded-bl-none'
                              }`}>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-semibold text-sm">
                                    {message.sender_name}
                                  </span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    message.sender_role === 'admin' 
                                      ? 'bg-red-100 text-red-800' :
                                      message.sender_role === 'manager'
                                      ? 'bg-purple-100 text-purple-800' :
                                      message.sender_role === 'freelancer'
                                      ? 'bg-green-100 text-green-800' :
                                      'bg-blue-100 text-blue-800'
                                  }`}>
                                    {message.sender_role}
                                  </span>
                                </div>
                                <p className="whitespace-pre-wrap">{message.message}</p>
                                <div className={`text-xs mt-2 ${
                                  isOwnMessage ? 'text-blue-700' : 'text-gray-500'
                                }`}>
                                  {formatTime(message.created_at)}
                                  {message.is_read && isOwnMessage && (
                                    <span className="ml-2">✓ Read</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Message Input */}
              {canSendMessages() ? (
                <div className="p-6 border-t border-gray-200">
                  <form onSubmit={handleSendMessage} className="flex gap-3">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 form-input"
                      disabled={isSending}
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim() || isSending}
                      className="btn btn-primary"
                    >
                      {isSending ? 'Sending...' : 'Send'}
                    </button>
                  </form>
                  <div className="text-xs text-gray-500 mt-2">
                    Press Enter to send • Be professional and clear in your communication
                  </div>
                </div>
              ) : (
                <div className="p-6 border-t border-gray-200 bg-yellow-50">
                  <p className="text-yellow-700 text-center">
                    {user.role === 'client' 
                      ? 'You can send messages once a freelancer is assigned to this project.'
                      : 'You can send messages once you are assigned to this project.'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Chat Tips */}
          <div className="mt-6 p-6 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3">Chat Etiquette Tips</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• Be clear and specific about project requirements and updates</li>
              <li>• Respond in a timely manner to keep the project moving forward</li>
              <li>• Keep all communication professional and respectful</li>
              <li>• Use this chat for project-related discussions only</li>
              <li>• Important decisions should be documented in project updates</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectMessaging;